/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import BarPlot from "./FullReports/BarPlot"
import CartesianPlot from "./FullReports/CartesianPlot"
import { EReportSectionTypes, EReportTypes, IArgosReportSection, ReportData, ReportTab } from "./constants"
import FullReport from "./FullReports/FullReport"
import Image from "./FullReports/Image"
import StaticReport from "./StaticReports/StaticReport"
import Table from "./FullReports/Table"
import Text from "./FullReports/Text"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages, ReportPages, VehicleProgramsReportPages } from "../../pages/AdasPrograms/ItemDetails/common/constants"
import ArgosFlexbox from "../Containers/ArgosFlexbox"
import { HorizontalTabs } from "../Tabs/HorizontalTabs"
import Loader from "../Loader/Loader"
import { useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { viewReportDialogSlice } from "../../pages/AdasPrograms/ItemDetails/common/Dialogs/ViewReportDialog"
import { getUrlForDataset } from "../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties"
import useGetUrlParams from "../../../hooks/useGetUrlParams"
import { usePostRequest } from "../../../api/helper"
import toast from "react-hot-toast"
import ADAS_PROGRAMS_CONSTANTS from "../../pages/AdasPrograms/constants"
import { StringParam, useQueryParam } from "use-query-params"
import { getPageFromHtabIndex } from "../../pages/AdasPrograms/ItemDetails/common/helpers"


function getFullReportKey(page: ReportPages | AdasProgramsReportPages | VehicleProgramsReportPages): string {
  if (
    page === ReportPages.SENSING_SYSTEM_REQUIREMENT ||
    page === ReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT ||
    page === ReportPages.SENSOR_COMPONENT_REQUIREMENT
  ) {
    return 'argosReport';
  } else {
    return 'fullReport';
  }
}

function getStaticReportKey(page: ReportPages | AdasProgramsReportPages | VehicleProgramsReportPages): string {
  if (
    page === ReportPages.SENSING_SYSTEM_REQUIREMENT ||
    page === ReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT ||
    page === ReportPages.SENSOR_COMPONENT_REQUIREMENT
  ) {
    return 'jamaReport';
  } else {
    return 'staticReport';
  }
}

export function createReportHorizontalTabs(reportData: ReportData, page: ReportPages | AdasProgramsReportPages | VehicleProgramsReportPages): ReportTab[] {
  const tabs = []
  const reportId = reportData ? reportData?.reportId : ''
  const fullReportKey = getFullReportKey(page) as keyof ReportData
  const fullReportData = reportData ? reportData[fullReportKey] : []
  const staticReportKey = getStaticReportKey(page) as keyof ReportData
  const staticReportData = reportData ? reportData[staticReportKey] : {}


  let newComponentData: any = {}
  fullReportData.forEach((reportSection: IArgosReportSection) => {
    switch (reportSection.type) {
      case EReportSectionTypes.BAR_PLOT:
        newComponentData[reportSection.name] = <BarPlot data={reportSection.data}/>
        break;
      case EReportSectionTypes.CARTESIAN_PLOT:
        newComponentData[reportSection.name] = <CartesianPlot data={reportSection.data}/>
        break;
      case EReportSectionTypes.IMAGE:
        newComponentData[reportSection.name] = <Image data={reportSection.data}/>
        break;
      case EReportSectionTypes.TABLE:
        newComponentData[reportSection.name] = <Table data={reportSection.data}/>
        break;
      case EReportSectionTypes.TEXT:
        newComponentData[reportSection.name] = <Text data={reportSection.data}/>
        break;
    }
  })
  const fullReportTab: ReportTab = {
    "name": EReportTypes.FULL_REPORT,
    "content": <FullReport componentDataMap={newComponentData}/>
  }
  tabs.push(fullReportTab)

  const staticReportTab: ReportTab  = {
    "name": EReportTypes.STATIC_REPORT,
    "content": <StaticReport staticReportData={staticReportData} reportId={reportId}/>
  }
  tabs.push(staticReportTab)

  return tabs
}


export const ReportView = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const {adasProgramId} = useGetUrlParams()
  const {reportId} = useGetUrlParams()

  const reportContent = useAppSelector(viewReportDialogSlice[page].selectReportContent)

  const dataSet = reportContent?.selectedReportData?.selectedDataset
  const dataSetUrl = getUrlForDataset(1, dataSet?.studyId ?? '', dataSet?.id, page)
  const dataSetName = dataSet?.name ?? ''

  const [tabs, setTabs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const contentBelowStaticReportTab = useMemo(() => {
    /** NOTE: Sync report to DNG action will go here */
    return (<></>)
  }, [])

  const contentBelowTabs = useMemo(() => {
    return (
      {
        [EReportTypes.STATIC_REPORT]: contentBelowStaticReportTab,
      }
    )
  }, [contentBelowStaticReportTab])

  /** Fetch report content */
  const getReportHandle = usePostRequest(viewReportDialogSlice[page].getReportEndpoint);
  const getReport = () => {
    if (reportId == null) return
    setIsLoading(true)
    const body = {reportId, adasProgramId}
    getReportHandle(body).then((data) => {
      dispatch(viewReportDialogSlice[page].setReportContent(data))
      setTabs(createReportHorizontalTabs(data, page))
    }).catch((e) => {
      console.log(e)
      toast.error("An error occurred fetching the report content")
      setTabs([])
    }).finally(() => {
      setIsLoading(false)
    })
  }

  /** Fetch report */
  useEffect(() => {
    if (adasProgramId && page && reportId !== null) {
      getReport()
    } else {
      setIsLoading(false)
      setTabs([])
    }
  }, [adasProgramId, reportId, page])

  return (
    <ArgosFlexbox vertical style={{flex: 1, overflow: 'auto', padding: 0, height: '100%', width: '100%'}}>
      <Loader isLoading={isLoading}>
        <ArgosFlexbox vertical style={{flex: 1, overflow: 'auto', scrollbarGutter: 'stable'}}>
          <ArgosFlexbox style={{marginLeft: 20, marginTop: 10}}>
            <ArgosFlexbox style={{visibility: dataSetName?.length ? 'visible' : 'hidden'}}>
              {page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT ? "Green Line " :  "Merged "} Data Set: <a href={dataSetUrl} target={'_blank'}>{dataSetName}</a>
            </ArgosFlexbox>
          </ArgosFlexbox>
          <HorizontalTabs
            padding={{padding: '0px 10px 10px 10px'}}
            initContent={null}
            initHtabIndex={0}
            horizontalTabs={tabs}
            align={'left'}
            uid={'ihtabs-1'}
          />
        </ArgosFlexbox>
      </Loader>
    </ArgosFlexbox>
  )
}
